/**
 * Utility functions for image validation
 */

/**
 * Interface for image dimension requirements
 */
export interface ImageDimensionRequirements {
  width?: number;
  height?: number;
  minWidth?: number;
  minHeight?: number;
  aspectRatio?: number;
  aspectRatioTolerance?: number;
}

/**
 * Interface for validation result
 */
export interface ImageValidationResult {
  valid: boolean;
  message: string | null;
  details?: {
    actualWidth: number;
    actualHeight: number;
    actualAspectRatio: number;
    requiredWidth?: number;
    requiredHeight?: number;
    requiredAspectRatio?: number;
  };
}

/**
 * Validates image dimensions against requirements
 * @param file The image file to validate
 * @param requirements The dimension requirements
 * @returns Promise that resolves to validation result
 */
export const validateImageDimensions = (
  file: File,
  requirements: ImageDimensionRequirements
): Promise<ImageValidationResult> => {
  return new Promise((resolve) => {
    if (!file.type.startsWith('image/')) {
      resolve({
        valid: false,
        message: 'File is not an image',
      });
      return;
    }

    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      const width = img.width;
      const height = img.height;
      const actualAspectRatio = width / height;

      const details = {
        actualWidth: width,
        actualHeight: height,
        actualAspectRatio,
        requiredWidth: undefined as number | undefined,
        requiredHeight: undefined as number | undefined,
        requiredAspectRatio: undefined as number | undefined,
      };

      // Check exact dimensions if specified
      if (requirements.width && requirements.height) {
        details.requiredWidth = requirements.width;
        details.requiredHeight = requirements.height;

        if (width !== requirements.width || height !== requirements.height) {
          resolve({
            valid: false,
            message: `Image dimensions must be exactly ${requirements.width}x${requirements.height} pixels. Uploaded image is ${width}x${height} pixels.`,
            details,
          });
          return;
        }
      }

      // Check minimum dimensions
      if (requirements.minWidth && width < requirements.minWidth) {
        details.requiredWidth = requirements.minWidth;
        resolve({
          valid: false,
          message: `Image width must be at least ${requirements.minWidth} pixels. Uploaded image width is ${width} pixels.`,
          details,
        });
        return;
      }

      if (requirements.minHeight && height < requirements.minHeight) {
        details.requiredHeight = requirements.minHeight;
        resolve({
          valid: false,
          message: `Image height must be at least ${requirements.minHeight} pixels. Uploaded image height is ${height} pixels.`,
          details,
        });
        return;
      }

      // Check aspect ratio if specified
      if (requirements.aspectRatio) {
        details.requiredAspectRatio = requirements.aspectRatio;
        const tolerance = requirements.aspectRatioTolerance || 0.01; // Default 1% tolerance
        const lowerBound = requirements.aspectRatio - tolerance;
        const upperBound = requirements.aspectRatio + tolerance;

        if (actualAspectRatio < lowerBound || actualAspectRatio > upperBound) {
          // Special message for square images (aspect ratio = 1.0)
          let message = '';
          if (Math.abs(requirements.aspectRatio - 1.0) < 0.01) {
            message = `Image must be square (equal width and height). Current dimensions are ${width}x${height} pixels.`;
          } else {
            message = `Image aspect ratio must be ${requirements.aspectRatio.toFixed(
              2
            )}. Uploaded image aspect ratio is ${actualAspectRatio.toFixed(
              2
            )}.`;
          }

          resolve({
            valid: false,
            message,
            details,
          });
          return;
        }
      }

      // If we got here, the image is valid
      resolve({
        valid: true,
        message: null,
        details,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({
        valid: false,
        message: 'Failed to load image for validation',
      });
    };

    img.src = objectUrl;
  });
};

/**
 * Parse dimension requirements from a string like "750x300"
 * @param dimensionString String in format "WIDTHxHEIGHT"
 * @returns Dimension requirements object
 */
export const parseDimensionString = (
  dimensionString: string
): ImageDimensionRequirements | null => {
  const match = dimensionString.match(/(\d+)x(\d+)/);
  if (!match) return null;

  return {
    width: parseInt(match[1], 10),
    height: parseInt(match[2], 10),
  };
};

/**
 * Extract dimension requirements from a caption string
 * @param caption Caption string that might contain dimension information
 * @returns Dimension requirements object or null if no dimensions found
 */
export const extractDimensionsFromCaption = (
  caption: string
): ImageDimensionRequirements | null => {
  // Look for exact dimensions like "750x300 pixels"
  const exactMatch = caption.match(/(\d+)x(\d+)\s*pixels/i);
  if (exactMatch) {
    return {
      width: parseInt(exactMatch[1], 10),
      height: parseInt(exactMatch[2], 10),
    };
  }

  // Look for minimum dimensions like "at least 1024x1024 pixels"
  const minMatch = caption.match(/at least (\d+)x(\d+)\s*pixels/i);
  if (minMatch) {
    return {
      minWidth: parseInt(minMatch[1], 10),
      minHeight: parseInt(minMatch[2], 10),
    };
  }

  return null;
};
